import React, { useState, useEffect, forwardRef } from 'react';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import { validateData, type ErrorObject } from '../../providers/validateData';
import CustomTypography from '../Typography/CustomTypography';

interface Option {
  label: string;
  value: string;
}

interface ValidationError {
  validateRules?: 'required'[];
  onValidationError?: (error: ErrorObject | null) => void;
}

interface CustomRadioGroupProps extends ValidationError {
  id: string;
  options: Option[];
  selectedValue: string;
  className?: string;
  label?: string;
  onChange: (value: string) => void;
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';
  size?: 'small' | 'medium';
  disabled?: boolean;
  row?: boolean;
}

const CustomRadioGroup = forwardRef<HTMLDivElement, CustomRadioGroupProps>(
  (
    {
      id,
      options,
      selectedValue = '',
      className,
      label,
      onChange,
      color = 'primary',
      size = 'medium',
      disabled = false,
      row = false,
      validateRules,
      onValidationError,
    },
    ref
  ) => {
    const [value, setValue] = useState<string>(selectedValue);
    const [inputError, setInputError] = useState<ErrorObject | null>(null);

    useEffect(() => {
      setValue(selectedValue);
    }, [selectedValue]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setValue(newValue);
      onChange(newValue);

      if (validateRules) {
        const errors = validateData(newValue, validateRules);
        if (onValidationError) {
          onValidationError(errors);
        }
        setInputError(errors);
      }
    };

    return (
      <FormControl component="fieldset">
        {label && <FormLabel component="legend">{label}</FormLabel>}
        <RadioGroup value={value} onChange={handleChange} row={row} ref={ref}>
          {options.map((option) => (
            <FormControlLabel
              id={id}
              className={className}
              key={option.value}
              value={option.value}
              control={<Radio color={color} size={size} disabled={disabled} />}
              label={option.label}
            />
          ))}
        </RadioGroup>
        {inputError?.invalid && (
          <CustomTypography id={`${id}-error`} variant="h6" color="error">
            {inputError.message}
          </CustomTypography>
        )}
      </FormControl>
    );
  }
);

CustomRadioGroup.displayName = 'CustomRadioGroup';

export default CustomRadioGroup;
